import { useState } from 'react';
import { downloadLiveAttachment, AttachmentUnavailableError, type LiveAttachment } from '../api/chatClient';

/**
 * Download chips for files a live agent attached.
 *
 * The bytes are fetched through the API (which needs auth headers and holds the upstream credential),
 * so this cannot be a plain link — it fetches a blob and drives a synthetic download. Image previews are
 * deliberately not rendered inline: the content is agent-supplied and served from our own origin, so it
 * is only ever handed to the user as a download.
 */
export function LiveAttachmentChips({
  attachments,
  conversationId,
  getAuth,
}: {
  attachments: LiveAttachment[];
  conversationId: string | null;
  getAuth: () => Promise<AttachmentAuth>;
}) {
  return (
    <div style={styles.list}>
      {attachments.map((a, i) => (
        // Index-suffixed: OC can repeat an id within one message, and a duplicate React key would warn.
        <AttachmentChip key={`${a.fileId}-${i}`} attachment={a} conversationId={conversationId} getAuth={getAuth} />
      ))}
    </div>
  );
}

/** Caller-supplied credentials for the download request. */
export interface AttachmentAuth {
  authBearer?: string;
  homeAccountId?: string;
  username?: string;
}

function AttachmentChip({
  attachment,
  conversationId,
  getAuth,
}: {
  attachment: LiveAttachment;
  conversationId: string | null;
  getAuth: () => Promise<AttachmentAuth>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = attachment.fileName?.trim() || 'Attachment';

  const onDownload = async () => {
    if (!conversationId || busy) return;
    setBusy(true);
    setError(null);
    let objectUrl: string | null = null;
    try {
      // Acquired per click, not captured at render: an access token can expire while the chat sits open.
      const auth = await getAuth();
      const { blob, fileName } = await downloadLiveAttachment({
        conversationId,
        fileId: attachment.fileId,
        contentType: attachment.contentType,
        ...auth,
      });
      objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.fileName?.trim() || fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setError(e instanceof AttachmentUnavailableError ? unavailableMessage(e.reason) : 'Download failed.');
    } finally {
      // Revoking immediately would race the browser's own read of the blob on some engines.
      const created = objectUrl;
      if (created) setTimeout(() => URL.revokeObjectURL(created), 60_000);
      setBusy(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        style={{ ...styles.chip, ...(busy ? styles.chipBusy : null) }}
        onClick={() => void onDownload()}
        disabled={busy || !conversationId}
        title={label}
      >
        <span aria-hidden="true" style={styles.icon}>📎</span>
        <span style={styles.name}>{label}</span>
        <span style={styles.action}>{busy ? 'Downloading…' : 'Download'}</span>
      </button>
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}

/** Turns the server's machine-readable reason into something a customer can act on. */
function unavailableMessage(reason: string | null): string {
  switch (reason) {
    case 'session_ended':
      return 'This chat has ended, so the file is no longer available.';
    case 'expired':
      return 'This file has expired. Ask the agent to send it again.';
    case 'malware':
      return 'This file was blocked by a security scan.';
    case 'not_ready':
      return 'This file is still being processed. Try again in a moment.';
    case 'too_large':
      return 'This file is too large to download here.';
    case 'not_configured':
      return 'File downloads are not available for this chat.';
    default:
      return 'This file is unavailable.';
  }
}

const styles: Record<string, React.CSSProperties> = {
  list: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #d1d1d1',
    background: '#fff',
    cursor: 'pointer',
    font: 'inherit',
    fontSize: 13,
    textAlign: 'left',
  },
  chipBusy: { opacity: 0.7, cursor: 'default' },
  icon: { fontSize: 14, flex: '0 0 auto' },
  name: { flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  action: { flex: '0 0 auto', color: '#0067b8', fontWeight: 600 },
  error: { marginTop: 4, fontSize: 12, color: '#a80000' },
};
